<template>
    <div class="integration-item border rounded shadow-sm my-3 p-3">
        <!-- Header Row -->
        <div class="flex items-center justify-between">
            <div class="flex items-center">
                <img :src="logo" :alt="`${title} logo`" class="w-10 h-10 mr-4" />
                <div>
                    <div class="flex items-center">
                        <span class="text-lg font-medium">{{ title }}</span>
                        <CheckCircleIcon v-if="isConnected" class="h-5 w-5 text-green-700 ml-2" />
                    </div>
                    <div class="text-sm text-gray-600 italic">
                        <span v-if="setInput && !showInput">{{ setInput }}</span>
                        <span v-else-if="!setInput">Address</span>
                    </div>
                </div>
            </div>
            <!-- Action Button in header row -->
            <div class="flex items-center">
                <div v-if="loading" class="mr-2">
                    <HalfCircleSpinner :loading="true" />
                </div>
                <button
                    @click="toggleConnection"
                    :disabled="loading"
                    :class="[
                        'px-4 py-2 font-medium transition text-white rounded-lg',
                        isConnected ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                    ]">
                    {{ isConnected ? 'Disconnect' : 'Connect' }}
                </button>
            </div>
        </div>
        <!-- Configuration Row: displayed only when editing -->
        <div v-if="showInput" class="mt-3 border-t pt-3">
            <input
                v-model="inputValue"
                type="text"
                class="border p-2 rounded-lg w-full mb-2"
                :class="{ 'border-red-500': inputValue && !isValidEmail(inputValue) }"
                :placeholder="placeholder || 'Enter email address'"
                :disabled="loading" />
            <div class="flex justify-end space-x-2">
                <div v-if="loading" class="mr-2">
                    <HalfCircleSpinner :loading="true" />
                </div>
                <button
                    @click="saveInput"
                    :disabled="loading || !isValidEmail(inputValue)"
                    class="px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white disabled:opacity-50">
                    Save
                </button>
                <button
                    @click="cancelInput"
                    :disabled="loading"
                    class="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50">
                    Cancel
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import HalfCircleSpinner from '../../global/loader/HalfCircleSpinner.vue';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import * as notificationsApi from '../../../api/notifications.api';
import { isValidEmail as isValidEmailUtil } from '../../../utils/validators';

export default {
    name: 'InputIntegrationType',
    components: {
        HalfCircleSpinner,
        CheckCircleIcon
    },
    props: {
        title: { type: String, required: true },
        logo: { type: String, required: true },
        isConnected: { type: Boolean, default: false },
        placeholder: { type: String, default: null },
        setInput: { type: String, default: null },
        subscriberId: { type: String, default: null },
        providerId: { type: String, default: null }
    },
    data() {
        return {
            showInput: false,
            loading: false,
            inputValue: this.setInput || ''
        };
    },
    methods: {
        isValidEmail(input) {
            return isValidEmailUtil(input);
        },
        async toggleConnection() {
            this.loading = true;
            try {
                const connected = !this.isConnected;
                if (connected) {
                    // Show configuration row on connect
                    this.showInput = true;
                } else {
                    // Disconnect: clear the input and update connection
                    await notificationsApi.deleteSubscriberCredentials(this.subscriberId, this.providerId);
                    await notificationsApi.updateSubscriber(this.subscriberId, '');
                    this.$emit('update:setInput', null);
                    this.inputValue = '';
                    this.showInput = false;
                    this.$emit('update:isConnected', false);
                }
            } catch (error) {
                console.error('Error toggling email connection:', error);
            } finally {
                this.loading = false;
            }
        },
        async saveInput() {
            this.loading = true;
            try {
                await notificationsApi.updateSubscriber(this.subscriberId, this.inputValue);
                this.$emit('update:setInput', this.inputValue);
                this.$emit('update:isConnected', true);
                this.showInput = false;
            } catch (error) {
                console.error('Failed to update subscriber:', error);
            } finally {
                this.loading = false;
            }
        },
        cancelInput() {
            this.inputValue = this.setInput || '';
            this.$emit('update:isConnected', false);
            this.showInput = false;
        }
    }
};
</script>

<style scoped>
.integration-item {
    margin: 1rem 0;
    padding: 1rem;
}
</style>
